import React, { useMemo, useRef, useEffect } from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const RequirementsTickerWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';
  const scrollRef = useRef<HTMLDivElement>(null);

  const requirements = state?.requirements ?? null;

  const tickerItems = useMemo(() => {
    if (!requirements) return [];

    const items: Array<{ type: 'persona' | 'story'; text: string; isNew: boolean }> = [];

    for (const persona of requirements.personas) {
      items.push({
        type: 'persona',
        text: `${persona.name} — ${persona.role}`,
        isNew: persona.isNew ?? false,
      });
    }

    for (const story of requirements.userStories) {
      items.push({
        type: 'story',
        text: story.story,
        isNew: story.isNew ?? false,
      });
    }

    return items;
  }, [requirements]);

  // Reset animation on new items
  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      el.style.animation = 'none';
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- reading offsetHeight forces a synchronous layout reflow; the value itself is intentionally unused
      el.offsetHeight; // NOSONAR: forces layout reflow to restart the CSS animation, not a bug
      el.style.animation = '';
    }
  }, [tickerItems.length]);

  if (!state) return null;

  if (tickerItems.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No requirements extracted yet
      </div>
    );
  }

  const renderItems = tickerItems.map((item) => (
    <span key={`${item.type}-${item.text.slice(0, 20)}`} className="inline-flex items-center mx-6 shrink-0">
      <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded mr-2 ${
        item.type === 'persona'
          ? 'bg-purple-500/20 text-purple-400'
          : 'bg-primary/20 text-primary'
      }`}>
        {item.type === 'persona' ? 'Persona' : 'Story'}
      </span>
      {item.isNew && (
        <span className="text-xs font-bold bg-amber-500/20 text-amber-400 px-1 py-0.5 rounded mr-2">
          NEW
        </span>
      )}
      <span className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
        {item.text.length > 100 ? item.text.slice(0, 100) + '...' : item.text}
      </span>
      <span className={`mx-4 ${isDark ? 'text-white/10' : 'text-slate-200'}`}>•</span>
    </span>
  ));

  return (
    <div className="relative h-full overflow-hidden flex items-center">
      {/* Edge fades */}
      <div className={`absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none ${
        isDark ? 'bg-gradient-to-r from-[#0b1120] to-transparent' : 'bg-gradient-to-r from-white to-transparent'
      }`} />
      <div className={`absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none ${
        isDark ? 'bg-gradient-to-l from-[#0b1120] to-transparent' : 'bg-gradient-to-l from-white to-transparent'
      }`} />

      <div
        ref={scrollRef}
        className="flex whitespace-nowrap"
        style={{
          animation: `req-crawl ${Math.max(15, tickerItems.length * 6)}s linear infinite`,
        }}
      >
        {renderItems}
        {renderItems}
      </div>

      <style>{`
        @keyframes req-crawl {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};

export default RequirementsTickerWidget;
